import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PROCEEDINGS_PLEAS,
  type DocketEntry,
  type ProceedingsPlea,
  type UpdateProceedingsDto,
} from '@munifocus/contracts-gov';
import { useUpdateProceedings } from '../lib/proceedings-api';

/** A `<input type="date">` wants `YYYY-MM-DD`; the docket carries ISO timestamps. */
function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/**
 * P3 — post-filing case management for one docket row (brief §9.2, columns
 * 7-12). Filing a matter (P1) fills the first six columns automatically; these
 * five are the officer's running record of what the court actually did, so the
 * dialog is deliberately all-optional — a matter that has been adjourned with no
 * plea taken is a normal, saveable state.
 *
 * Every field is sent on save, `null` where blank, so clearing a vacated court
 * date is possible rather than sticky.
 */
export function CaseManagementDialog({
  entry,
  open,
  onClose,
}: {
  entry: DocketEntry;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateProceedings(entry.id, entry.fileId);

  // Seeded once, from the row. The caller mounts this dialog only while a row is
  // being managed (and unmounts it on close), so a fresh mount re-seeds from the
  // freshly-invalidated docket — no abandoned edit can leak into the next row.
  const [firstAppearance, setFirstAppearance] = useState(() => toDateInput(entry.firstAppearance));
  const [returnableDate, setReturnableDate] = useState(() => toDateInput(entry.returnableDate));
  const [plea, setPlea] = useState<ProceedingsPlea | ''>(entry.plea ?? '');
  const [fineImposed, setFineImposed] = useState(entry.fineImposed ?? '');
  const [notes, setNotes] = useState(entry.notes ?? '');

  // Mirrors the server-side rule so the officer is told before the round trip.
  const datesOutOfOrder =
    firstAppearance !== '' && returnableDate !== '' && returnableDate < firstAppearance;

  const onSave = () => {
    const dto: UpdateProceedingsDto = {
      firstAppearance: firstAppearance || null,
      returnableDate: returnableDate || null,
      plea: plea || null,
      fineImposed: fineImposed.trim() || null,
      notes: notes.trim() || null,
    };
    update.mutate(dto, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
        {t('gov.docket.manage.title')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('gov.docket.manage.intro', {
              reference: entry.reference,
              courtFileNumber: entry.courtFileNumber,
            })}
          </Typography>

          {update.isError && <Alert severity="error">{t('gov.docket.manage.saveError')}</Alert>}

          <TextField
            fullWidth
            size="small"
            type="date"
            label={t('gov.docket.col.firstAppearance')}
            slotProps={{ inputLabel: { shrink: true } }}
            value={firstAppearance}
            onChange={(e) => setFirstAppearance(e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label={t('gov.docket.col.returnableDate')}
            slotProps={{ inputLabel: { shrink: true } }}
            value={returnableDate}
            onChange={(e) => setReturnableDate(e.target.value)}
            error={datesOutOfOrder}
            helperText={datesOutOfOrder ? t('gov.docket.manage.datesOutOfOrder') : undefined}
          />

          <TextField
            select
            fullWidth
            size="small"
            label={t('gov.docket.col.plea')}
            value={plea}
            onChange={(e) => setPlea(e.target.value as ProceedingsPlea | '')}
          >
            <MenuItem value="">{t('gov.docket.manage.pleaNotRecorded')}</MenuItem>
            {PROCEEDINGS_PLEAS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`gov.docket.plea.${value}`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            size="small"
            label={t('gov.docket.col.fineImposed')}
            placeholder={t('gov.docket.manage.finePlaceholder')}
            value={fineImposed}
            onChange={(e) => setFineImposed(e.target.value)}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('gov.docket.col.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" variant="outlined" onClick={onClose}>
          {t('common.actions.cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={datesOutOfOrder || update.isPending}
          onClick={onSave}
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
